import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import Button from '../Button'
import { isSuperAdmin, isAdmin } from '../../../utils/roleUtils'
import { ChevronDown, Settings, ShieldCheck, Database, Users, History } from 'lucide-react'
import './styles.scss'

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showSuperAdminDropdown, setShowSuperAdminDropdown] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuperAdminDropdown(false)
      }
    }
    
    if (showSuperAdminDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSuperAdminDropdown])

  function handleLogout() {
    logout()
    navigate('/')
  }

  // Active link helper
  const isActive = (path) => location.pathname === path ? 'header-link--active' : ''

  return (
    <header className={`main-header${isAuthenticated ? ' with-sidebar' : ''}`}>
      <div className="header-glass-wrapper">
        <div className="header-content">

          <div className="header-brand" onClick={() => navigate('/')}>
            <div className="brand-logo" style={{ background: 'transparent', boxShadow: 'none' }}>
              <img src="/smartadvising.svg" alt="Smart Advising" style={{ width: '2.5rem', height: '2.5rem' }} />
            </div>
            <span className="brand-name">
              <span style={{ fontWeight: 800, color: '#1a202c' }}>Smart</span>
              <span style={{ fontWeight: 300 }}>Advising</span>
            </span>
          </div>

          <nav className="header-nav">
            <Link to="/" className={`header-link ${isActive('/')}`}>Home</Link>

            {isAuthenticated && (
              <>
                <span className="nav-delimiter">|</span>
                <Link to="/workforce-analytics" className={`header-link ${isActive('/workforce-analytics')}`}>Workforce Analytics</Link>
                <span className="nav-delimiter">|</span>
                <Link to="/academic-hub" className={`header-link ${isActive('/academic-hub')}`}>Pathway Design</Link>
                {isAdmin(user) && (
                  <>
                    <span className="nav-delimiter">|</span>
                    <Link to="/institutional-executions" className={`header-link ${isActive('/institutional-executions')}`}>
                      Monitor <span className="admin-badge" data-tooltip="Institutional Admin Access">A</span>
                    </Link>
                    <span className="nav-delimiter">|</span>
                    <Link to="/partner-integration" className={`header-link ${isActive('/partner-integration')}`}>
                      Integrations <span className="admin-badge" data-tooltip="Partner Embed Generator (Admin)">A</span>
                    </Link>
                  </>
                )}
                {isSuperAdmin(user) && (
                  <div className="header-dropdown-container" ref={dropdownRef}>
                    <button
                      className={`header-link dropdown-trigger ${isActive('/datasets') || isActive('/users') || isActive('/executions') ? 'header-link--active' : ''}`}
                      onClick={() => setShowSuperAdminDropdown(!showSuperAdminDropdown)}
                    >
                      <ShieldCheck size={16} />
                      <span>Super Admin Hub</span>
                      <ChevronDown size={14} className={showSuperAdminDropdown ? 'rotate-180' : ''} />
                    </button>

                    {showSuperAdminDropdown && (
                      <div className="header-dropdown-menu">
                        <Link to="/datasets" className="dropdown-item" onClick={() => setShowSuperAdminDropdown(false)}>
                          <Database size={14} />
                          <span>Datasets</span>
                          <span className="admin-badge" data-tooltip="Super Admin Exclusive Access">SA</span>
                        </Link>
                        <Link to="/users" className="dropdown-item" onClick={() => setShowSuperAdminDropdown(false)}>
                          <Users size={14} />
                          <span>User Management</span>
                          <span className="admin-badge" data-tooltip="Super Admin Exclusive Access">SA</span>
                        </Link>
                        <Link to="/executions" className="dropdown-item" onClick={() => setShowSuperAdminDropdown(false)}>
                          <History size={14} />
                          <span>Global History</span>
                          <span className="admin-badge" data-tooltip="Super Admin Exclusive Access">SA</span>
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </nav>

          <div className="header-actions">
            {isAuthenticated ? (
              <div className="user-profile">
                <Link to="/profile" className="user-badge profile-trigger-link" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <span className="user-icon-sm">👤</span>
                  <span className="user-name">{user?.username || 'User'}</span>
                </Link>
                <Button variant="ghost" size="sm" className="btn-logout-header" onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            ) : (
              <div className="header-auth-buttons">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="btn-login-header"
                >
                  Login
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/register')}
                  className="btn-register-header"
                >
                  Register
                </Button>
              </div>
            )}
          </div>

        </div>
      </div>
    </header>
  )
}
